//External Lib Import
import { useState, useEffect, useRef } from "react";
import { Container, Navbar } from "react-bootstrap";
import {
  AiOutlineLogout,
  AiOutlineMenuUnfold,
  AiOutlineSetting,
  AiOutlineUser,
} from "react-icons/ai";
import { BsArrowsFullscreen } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

//Internal Lib Import
import shunno_logo from "../assets/images/shunno_logo.png";
import { useProfileDetailsQuery } from "../redux/services/profileService";
import defaultUserAvatar from "../assets/images/user.png";
import { setLogout } from "../redux/slice/authReducer";
import { useTranslation } from "react-i18next";
import LanguageDropdown from "../components/languageDropdown";

const Navigation = ({ openMenu, setOpenMenu, title = "Home" }) => {
  const { t } = useTranslation();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { accessToken } = useSelector((state) => state.authReducer);
  const dispatch = useDispatch();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const FullScreen = () => {
    if (isFullScreen === true) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setIsFullScreen(false);
    } else {
      let elem = document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
      setIsFullScreen(true);
    }
  };

  const { data: profileDetails } = useProfileDetailsQuery();

  // console.log("profileDetails", profileDetails);

  const logout = () => {
    setIsLoggingOut(true);
    setTimeout(() => {
      const result = dispatch(setLogout());
      if (result) window.location.reload();
      else setIsLoggingOut(false);
    }, 800); // Add small delay to show loading effect
  };

  return (
    <>
      <title>{title}</title>
      <Navbar className="fixed-top px-0 shadow-sm ">
        <Container fluid={true}>
          <Navbar.Brand>
            <button
              className="icon-nav m-0 h5 btn btn-link"
              onClick={() => setOpenMenu(!openMenu)}
            >
              <AiOutlineMenuUnfold />
            </button>
            <Link to="/">
              <img
                className="nav-logo mx-2 "
                src={shunno_logo}
                alt="logo"
                style={{ height: "35px", width: "auto" }}
              />
            </Link>
          </Navbar.Brand>

          <div className="float-right h-auto d-flex">
            <div>
              <LanguageDropdown />
            </div>
            <button
              className="mx-2 icon-nav h6 px-3 btn btn-link"
              onClick={FullScreen}
            >
              <BsArrowsFullscreen />
            </button>

            {accessToken && profileDetails && (
              <div
                className="user-dropdown position-relative"
                ref={dropdownRef}
              >
                <div
                  className="d-flex align-items-center cursor-pointer  border border-light-subtle rounded-pill px-1 py-1 hover-effect"
                  onClick={() => setOpenDropdown(!openDropdown)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 8px rgba(0,0,0,0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 4px rgba(0,0,0,0.05)";
                  }}
                >
                  <img
                    className="rounded-circle border border-2 border-white shadow-sm"
                    src={profileDetails?.photo || defaultUserAvatar}
                    alt={profileDetails?.email}
                    width="28"
                    height="28"
                    style={{ objectFit: "cover" }}
                  />
                  <div className="ms-2 d-none d-md-block">
                    <span className="fw-medium text-dark">
                      {profileDetails?.name?.toUpperCase() || "User"}
                    </span>
                  </div>
                  <div className="ms-2 text-muted">
                    <i
                      className="fa fa-chevron-down"
                      style={{ fontSize: "10px" }}
                    ></i>
                  </div>
                </div>

                {openDropdown && (
                  <div
                    className="position-absolute end-0 mt-2 py-2 bg-white rounded-3 shadow-lg border"
                    style={{ minWidth: "220px", zIndex: 1000 }}
                  >
                    <div className="px-4 py-3 border-bottom">
                      <div className="d-flex align-items-center">
                        <img
                          className="rounded-circle border border-2 border-light"
                          src={profileDetails?.photo || defaultUserAvatar}
                          alt={profileDetails?.email}
                          width="40"
                          height="40"
                        />
                        <div className="ms-3">
                          <h6 className="mb-0 fw-semibold">
                            {profileDetails?.name}
                          </h6>
                        </div>
                      </div>
                    </div>

                    <div className="py-1">
                      {/* Profile Option */}
                      <Link
                        to="/profile"
                        className="dropdown-item d-flex align-items-center px-3 py-2 text-decoration-none text-body"
                      >
                        <AiOutlineUser className="me-2 text-muted" size={18} />
                        <span>Profile</span>
                      </Link>

                      {/* Settings Option */}
                      <Link
                        to="/settings"
                        className="dropdown-item d-flex align-items-center px-3 py-2 text-decoration-none text-body"
                      >
                        <AiOutlineSetting
                          className="me-2 text-muted"
                          size={18}
                        />
                        <span>Settings</span>
                      </Link>

                      {/* Logout Option */}
                      <div
                        onClick={logout}
                        className="dropdown-item d-flex align-items-center px-3 py-2 rounded-2 cursor-pointer text-danger"
                        style={{ opacity: isLoggingOut ? 0.7 : 1 }}
                      >
                        {isLoggingOut ? (
                          <div className="spinner-border spinner-border-sm me-2 text-danger">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          <AiOutlineLogout className="me-2" size={18} />
                        )}
                        <span>{isLoggingOut ? "logout" : t("logout")}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </Container>
      </Navbar>
    </>
  );
};

export default Navigation;
